import { useState } from "react";

const FORM_ID = "db7s8afidngsrf4ehp0g";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db7s8afidngsrf4ehp0g";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      id={FORM_ID}
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="text-base font-semibold text-foreground-950">在线留言与反馈</h3>
      <p className="mt-1.5 text-xs text-foreground-500">
        你的建议会帮助我们做得更好（本表单为演示用途，仅用于展示提交效果）。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className="mb-1.5 block text-sm text-foreground-700">
            称呼
          </label>
          <input
            id="cf-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className="h-11 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="cf-email" className="mb-1.5 block text-sm text-foreground-700">
            邮箱
          </label>
          <input
            id="cf-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复"
            className="h-11 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="cf-type" className="mb-1.5 block text-sm text-foreground-700">
          反馈类型
        </label>
        <select
          id="cf-type"
          name="type"
          defaultValue="功能建议"
          className="h-11 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400"
        >
          <option value="功能建议">功能建议</option>
          <option value="内容报错">内容报错</option>
          <option value="合作咨询">合作咨询</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="cf-message" className="mb-1.5 block text-sm text-foreground-700">
          留言内容
        </label>
        <textarea
          id="cf-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你的建议或问题（不超过 500 字）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        ></textarea>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-guard-check"
      />

      {status === "success" && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
          <i className="ri-checkbox-circle-line text-lg"></i>
          提交成功，感谢你的反馈！
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
          <i className="ri-error-warning-line text-lg"></i>
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <i className="ri-send-plane-line text-lg"></i>
        {status === "submitting" ? "提交中…" : "提交反馈"}
      </button>
    </form>
  );
}